import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import { FixedSizeList, ListChildComponentProps } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { HighlightedText } from "../searchUtils";

interface VirtualizedTableProps {
  rows: string[][];
  headerCells: string[];
  rowHeight?: number;
  minColumnWidth?: number;
  showRowNumbers?: boolean;
  searchQuery?: string;
  activeCell?: { row: number; col: number } | null;
  listRef?: React.MutableRefObject<FixedSizeList | null>;
}

const ROW_HEIGHT_DEFAULT = 28;
const MIN_COL_WIDTH_DEFAULT = 120;
const ROW_INDEX_WIDTH = 56;

const VirtualizedTable: React.FC<VirtualizedTableProps> = ({
  rows,
  headerCells,
  rowHeight = ROW_HEIGHT_DEFAULT,
  minColumnWidth = MIN_COL_WIDTH_DEFAULT,
  showRowNumbers = true,
  searchQuery = "",
  activeCell = null,
  listRef,
}) => {
  const headerScrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollLeft, setScrollLeft] = useState(0);

  const columnWidths = useMemo(() => {
    const colCount = headerCells.length;
    const widths: number[] = new Array(colCount).fill(minColumnWidth);
    const sample = rows.slice(0, 50);
    for (let c = 0; c < colCount; c++) {
      const headerLen = (headerCells[c] || "").length;
      let max = headerLen;
      for (const row of sample) {
        const cell = row[c];
        if (cell != null) {
          const len = String(cell).length;
          if (len > max) max = len;
        }
      }
      widths[c] = Math.min(
        Math.max(minColumnWidth, max * 7 + 24),
        420,
      );
    }
    return widths;
  }, [headerCells, rows, minColumnWidth]);

  const totalWidth = useMemo(
    () =>
      (showRowNumbers ? ROW_INDEX_WIDTH : 0) +
      columnWidths.reduce((s, w) => s + w, 0),
    [columnWidths, showRowNumbers],
  );

  useEffect(() => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = scrollLeft;
    }
  }, [scrollLeft]);

  const Row: React.FC<ListChildComponentProps> = ({ index, style }) => {
    const row = rows[index] || [];
    const isEven = index % 2 === 0;
    const isActiveRow = activeCell?.row === index;
    return (
      <div
        style={{
          ...style,
          display: "flex",
          width: totalWidth,
          backgroundColor: isActiveRow
            ? "rgba(255,152,0,0.10)"
            : isEven
              ? "#fff"
              : "#fafafa",
          borderBottom: "1px solid #eee",
        }}
      >
        {showRowNumbers && (
          <div
            style={{
              width: ROW_INDEX_WIDTH,
              flexShrink: 0,
              padding: "4px 8px",
              fontSize: 12,
              fontWeight: 700,
              textAlign: "center",
              color: "#666",
              borderRight: "1px solid #eee",
              backgroundColor: "#f5f5f5",
              position: "sticky",
              left: 0,
              zIndex: 1,
              boxSizing: "border-box",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {index + 1}
          </div>
        )}
        {columnWidths.map((w, c) => {
          const value = row[c] != null ? String(row[c]) : "";
          const isActiveCell =
            isActiveRow && activeCell?.col === c;
          return (
            <div
              key={c}
              style={{
                width: w,
                flexShrink: 0,
                padding: "4px 8px",
                fontSize: 12,
                borderRight: "1px solid #eee",
                boxSizing: "border-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                backgroundColor: isActiveCell
                  ? "rgba(255,152,0,0.25)"
                  : undefined,
                outline: isActiveCell
                  ? "2px solid #ff9800"
                  : undefined,
                outlineOffset: isActiveCell ? "-2px" : undefined,
              }}
              title={value}
            >
              {searchQuery ? (
                <HighlightedText text={value} query={searchQuery} />
              ) : (
                value
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
      }}
    >
      <Box
        ref={headerScrollRef}
        sx={{
          overflow: "hidden",
          borderBottom: "2px solid",
          borderColor: "divider",
          backgroundColor: (t) => t.palette.grey[100],
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: totalWidth,
          }}
        >
          {showRowNumbers && (
            <Box
              sx={{
                width: ROW_INDEX_WIDTH,
                flexShrink: 0,
                p: "6px 8px",
                fontSize: 12,
                fontWeight: 700,
                textAlign: "center",
                borderRight: "1px solid",
                borderColor: "divider",
                backgroundColor: (t) => t.palette.grey[200],
                position: "sticky",
                left: 0,
                zIndex: 2,
                boxSizing: "border-box",
              }}
            />
          )}
          {columnWidths.map((w, c) => (
            <Box
              key={c}
              sx={{
                width: w,
                flexShrink: 0,
                p: "6px 8px",
                fontSize: 12,
                fontWeight: 700,
                borderRight: "1px solid",
                borderColor: "divider",
                boxSizing: "border-box",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={headerCells[c]}
            >
              {searchQuery ? (
                <HighlightedText
                  text={headerCells[c] || ""}
                  query={searchQuery}
                />
              ) : (
                headerCells[c]
              )}
            </Box>
          ))}
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          "& > div": {
            "&::-webkit-scrollbar": { width: 10, height: 10 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.25)",
              borderRadius: 4,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(0,0,0,0.4)",
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
          },
        }}
      >
        <AutoSizer>
          {({ height, width }: { height: number; width: number }) => (
            <FixedSizeList
              ref={(node) => {
                if (listRef) listRef.current = node;
              }}
              height={height}
              width={width}
              itemCount={rows.length}
              itemSize={rowHeight}
              overscanCount={8}
              onScroll={({ scrollUpdateWasRequested }) => {
                if (scrollUpdateWasRequested) return;
              }}
              outerRef={(el: HTMLDivElement | null) => {
                if (!el) return;
                el.onscroll = () => {
                  setScrollLeft(el.scrollLeft);
                };
              }}
            >
              {Row}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
};

export default VirtualizedTable;
